/**
 * @pj/engine/art — runtime-generated GBA-style pixel art.
 *
 * Everything is drawn at 1x (16px tiles) onto 2D canvases and wrapped in Pixi
 * textures with nearest-neighbor sampling; the world container scales 3x.
 * Palettes follow Gen-3 (Emerald) color discipline: every channel a multiple
 * of 8 (15-bit GBA color), 4-tone ramps, dark-navy outlines instead of black.
 */
import { Texture, TextureStyle } from "pixi.js";

// Crisp pixels for every texture created after this module loads.
TextureStyle.defaultOptions.scaleMode = "nearest";

// --- GBA-legal palette ramps (from the pokeemerald tileset .pal files) -------

export const PAL = {
  // lawn / town green (Emerald mint)
  lawn: "#73C5A4",
  lawnLight: "#A4D5C5",
  lawnDark: "#41B483",
  lawnDarkest: "#18A46A",
  // foliage (trees, tall grass)
  leafHi: "#B4FF83",
  leaf: "#83C562",
  leafDark: "#398B31",
  leafDarkest: "#395200",
  // water
  waterHi: "#73BDF6",
  waterRipple: "#62ACEE",
  waterMid: "#62A4DE",
  water: "#528BC5",
  waterDark: "#4A73AC",
  glint: "#BDBD83",
  foam: "#FFFFFF",
  foam2: "#DEE6EE",
  foam3: "#BDCDE6",
  foam4: "#9CB4DE",
  // path / sand
  sandHi: "#EEE6A4",
  sand: "#DECD83",
  sandShade: "#D5B46A",
  sandDark: "#CD9C52",
  // roof reds
  roofHi: "#FFBD83",
  roof: "#EE9473",
  roofShade: "#DE6A62",
  roofDark: "#CD4152",
  // warm wood
  woodHi: "#FFE6B4",
  woodLight: "#F6A483",
  wood: "#CD8B6A",
  woodShade: "#94836A",
  woodDark: "#625239",
  // cool wood / trunks
  trunk: "#6A5A5A",
  trunkDark: "#413931",
  // plaster walls
  plaster: "#EEE6CD",
  plasterShade: "#D5CD94",
  plasterDark: "#CDB47B",
  plasterDarkest: "#AC8B7B",
  // window glass
  glassHi: "#9CCDF6",
  glass: "#629CEE",
  // skin
  skinHi: "#FFD5B4",
  skin: "#FFC594",
  skinShade: "#DE9473",
  // navy (outline, blob, interiors)
  navyLight: "#394A7B",
  navy: "#293962",
  navyDark: "#182952",
  outline: "#102039",
  // accents
  red: "#FF625A",
  redDark: "#C54141",
  pink: "#F8A0C8",
  pinkDark: "#C86890",
  goldHi: "#F8E080",
  gold: "#D8B048",
  goldDark: "#A88020",
  ballRed: "#CD624A",
  ballRedDark: "#943929",
  ballOutline: "#521000",
  ballWhiteShade: "#CDCDDE",
  discOuter: "#4AA4EE",
  discMid: "#8BC5EE",
  discInner: "#CDE6F6",
  interior: "#29315A",
  shadow: "#4A4A7B",
} as const;

export const TILE = 16;

// --- canvas + pixel-map helpers ----------------------------------------------

export function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  return [c, ctx];
}

export function px(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, 1, 1);
}

export function rect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
}

export type PixMap = readonly string[];

/** Paint a string pixel map ('.'=transparent) at (ox,oy). Tolerant of short rows. */
export function drawPixMap(
  ctx: CanvasRenderingContext2D,
  map: PixMap,
  colors: Record<string, string>,
  ox = 0,
  oy = 0,
): void {
  for (let y = 0; y < map.length; y++) {
    const row = map[y]!;
    for (let x = 0; x < row.length; x++) {
      const ch = row[x]!;
      if (ch === ".") continue;
      const color = colors[ch];
      if (!color) continue;
      px(ctx, ox + x, oy + y, color);
    }
  }
}

export function textureFromPixMap(
  map: PixMap,
  colors: Record<string, string>,
  w: number,
  h: number,
): Texture {
  const [canvas, ctx] = makeCanvas(w, h);
  drawPixMap(ctx, map, colors);
  return Texture.from(canvas);
}

/** Deterministic per-coordinate hash for stable texture noise. */
export function noise(x: number, y: number, salt = 0): number {
  let h = (x * 374761393 + y * 668265263 + salt * 2147483647) >>> 0;
  h = (h ^ (h >> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return ((h ^ (h >> 16)) >>> 0) / 4294967296;
}

// --- character sprite ---------------------------------------------------------

const PLAYER_COLORS: Record<string, string> = {
  O: PAL.outline,
  H: PAL.trunkDark,
  h: PAL.trunk,
  S: PAL.skin,
  s: PAL.skinShade,
  E: PAL.outline,
  R: PAL.red,
  r: PAL.redDark,
  N: PAL.navy,
  n: PAL.navyDark,
  B: PAL.trunkDark,
  W: PAL.foam2,
};

// Heads (rows 0-9), torsos (10-16), legs (17-20). 16 wide, 21 tall, feet at
// the bottom — drawn into a 16x32 box so the head overlaps the tile above,
// exactly like the GBA 16x32 overworld sprites.
const HEAD_DOWN: PixMap = [
  "....OOOOOOO.....",
  "...OHHHHHHHO....",
  "..OHHHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "..OHHSSSSSHHO...",
  "..OSSSSSSSSSO...",
  "..OSESSSSSESO...",
  "..OSSSSSSSSSO...",
  "...OSSSSSSSO....",
  "....OOOOOOO.....",
];
const HEAD_UP: PixMap = [
  "....OOOOOOO.....",
  "...OHHHHHHHO....",
  "..OHHHHHHHHHO...",
  "..OHhHHHHHhHO...",
  "..OHHHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "..OHhHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "...OHHHHHHHO....",
  "....OOOOOOO.....",
];
const HEAD_LEFT: PixMap = [
  "....OOOOOOO.....",
  "...OHHHHHHHO....",
  "..OHHHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "..OSSSSHHHHHO...",
  "..OSSSSSHHHHO...",
  "..OSESSSHHHHO...",
  "..OSSSSSHHHHO...",
  "...OSSSSHHHO....",
  "....OOOOOOO.....",
];

const TORSO_FRONT: PixMap = [
  "...ORRRRRRRO....",
  "..ORRRRRRRRRO...",
  "..ORRRRRRRRRO...",
  "..OSRRRRRRRSO...",
  "...ORRRRRRRO....",
  "...ONNNNNNNO....",
  "...ONNNONNNO....",
];
const TORSO_LEFT: PixMap = [
  "...ORRRRRRRO....",
  "...ORRRRRRRO....",
  "...OSRRRRRRO....",
  "...ORRRRRRRO....",
  "...ORRRRRRRO....",
  "...ONNNNNNNO....",
  "...ONNNNNNNO....",
];

const LEGS_FRONT_STAND: PixMap = [
  "...OSSO.OSSO....",
  "...OSSO.OSSO....",
  "...OBBO.OBBO....",
  "...OOOO.OOOO....",
];
const LEGS_FRONT_A: PixMap = [
  "...OSSO.OSSO....",
  "...OSSO.OBBO....",
  "...OBBO.OOOO....",
  "...OOOO.........",
];
const LEGS_FRONT_B: PixMap = [
  "...OSSO.OSSO....",
  "...OBBO.OSSO....",
  "...OOOO.OBBO....",
  "........OOOO....",
];
const LEGS_LEFT_STAND: PixMap = [
  "....OSSSSO......",
  "....OSSSSO......",
  "....OBBBBO......",
  "....OOOOOO......",
];
const LEGS_LEFT_A: PixMap = [
  "...OSSOOSSO.....",
  "..OSSO..OSSO....",
  "..OBBO..OBBO....",
  "..OOOO..OOOO....",
];
const LEGS_LEFT_B: PixMap = [
  "....OSSOSSO.....",
  "....OSSOSSO.....",
  "....OBBOBBO.....",
  "....OOO.OOO.....",
];

function actorTexture(
  head: PixMap,
  torso: PixMap,
  legs: PixMap,
  colors: Record<string, string>,
): Texture {
  const [canvas, ctx] = makeCanvas(16, 32);
  // 21 rows of figure, feet on the bottom edge → start at y=11.
  drawPixMap(ctx, head, colors, 0, 11);
  drawPixMap(ctx, torso, colors, 0, 21);
  drawPixMap(ctx, legs, colors, 0, 28);
  return Texture.from(canvas);
}

export type Facing3 = "down" | "up" | "left";

export interface PlayerSprites {
  /** [stand, stepA, stepB] per facing; "right" renders as flipped "left". */
  walk: Record<Facing3, Texture[]>;
}

interface ActorHeads {
  down: PixMap;
  up: PixMap;
  left: PixMap;
}

function buildActor(heads: ActorHeads, colors: Record<string, string>): PlayerSprites {
  return {
    walk: {
      down: [
        actorTexture(heads.down, TORSO_FRONT, LEGS_FRONT_STAND, colors),
        actorTexture(heads.down, TORSO_FRONT, LEGS_FRONT_A, colors),
        actorTexture(heads.down, TORSO_FRONT, LEGS_FRONT_B, colors),
      ],
      up: [
        actorTexture(heads.up, TORSO_FRONT, LEGS_FRONT_STAND, colors),
        actorTexture(heads.up, TORSO_FRONT, LEGS_FRONT_A, colors),
        actorTexture(heads.up, TORSO_FRONT, LEGS_FRONT_B, colors),
      ],
      left: [
        actorTexture(heads.left, TORSO_LEFT, LEGS_LEFT_STAND, colors),
        actorTexture(heads.left, TORSO_LEFT, LEGS_LEFT_A, colors),
        actorTexture(heads.left, TORSO_LEFT, LEGS_LEFT_B, colors),
      ],
    },
  };
}

// --- AANYA ---------------------------------------------------------------------
// poopie's little sister: bun pigtails, pink top. The crying variant bakes
// tears (T) under the eyes — the sim swaps sprites, the engine stays dumb.

const AANYA_COLORS: Record<string, string> = {
  ...PLAYER_COLORS,
  R: PAL.pink,
  r: PAL.pinkDark,
  T: PAL.glassHi,
};

const A_HEAD_DOWN: PixMap = [
  "..OO.OOOOO.OO...",
  ".OHHOHHHHHOHHO..",
  "..OHHHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "..OHHSSSSSHHO...",
  "..OSSSSSSSSSO...",
  "..OSESSSSSESO...",
  "..OSSSSSSSSSO...",
  "...OSSSSSSSO....",
  "....OOOOOOO.....",
];
const A_HEAD_DOWN_CRY: PixMap = [
  "..OO.OOOOO.OO...",
  ".OHHOHHHHHOHHO..",
  "..OHHHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "..OHHSSSSSHHO...",
  "..OSSSSSSSSSO...",
  "..OSESSSSSESO...",
  "..OSTSSSSSTSO...",
  "...OSSSSSSSO....",
  "....OOOOOOO.....",
];
const A_HEAD_UP: PixMap = [
  "..OO.OOOOO.OO...",
  ".OHHOHHHHHOHHO..",
  "..OHHHHHHHHHO...",
  "..OHhHHHHHhHO...",
  "..OHHHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "..OHhHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "...OHHHHHHHO....",
  "....OOOOOOO.....",
];
const A_HEAD_LEFT: PixMap = [
  "....OOOOOO.OO...",
  "...OHHHHHHOHHO..",
  "..OHHHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "..OSSSSHHHHHO...",
  "..OSSSSSHHHHO...",
  "..OSESSSHHHHO...",
  "..OSSSSSHHHHO...",
  "...OSSSSHHHO....",
  "....OOOOOOO.....",
];
const A_HEAD_LEFT_CRY: PixMap = [
  "....OOOOOO.OO...",
  "...OHHHHHHOHHO..",
  "..OHHHHHHHHHO...",
  "..OHHHHHHHHHO...",
  "..OSSSSHHHHHO...",
  "..OSSSSSHHHHO...",
  "..OSESSSHHHHO...",
  "..OSTSSSHHHHO...",
  "...OSSSSHHHO....",
  "....OOOOOOO.....",
];

function buildPlayer(): PlayerSprites {
  return buildActor({ down: HEAD_DOWN, up: HEAD_UP, left: HEAD_LEFT }, PLAYER_COLORS);
}

function buildAanya(): PlayerSprites {
  return buildActor({ down: A_HEAD_DOWN, up: A_HEAD_UP, left: A_HEAD_LEFT }, AANYA_COLORS);
}

function buildAanyaCry(): PlayerSprites {
  return buildActor(
    { down: A_HEAD_DOWN_CRY, up: A_HEAD_UP, left: A_HEAD_LEFT_CRY },
    AANYA_COLORS,
  );
}

// --- object sprites -------------------------------------------------------------

const SURF_BLOB: PixMap = [
  "................",
  "................",
  "................",
  "................",
  "................",
  ".....OOOOOO.....",
  "...OONNNNNNOO...",
  "..ONNNNNNNNNNO..",
  ".ONNNNNNNNNNNNO.",
  ".ONNNNNNNNNNNNO.",
  ".ONnNNNNNNNNnNO.",
  ".ONnnNNNNNNnnNO.",
  "..ONnnnnnnnnNO..",
  "...OONNNNNNOO...",
  ".W..........W...",
  "................",
];
const BLOB_COLORS: Record<string, string> = {
  O: PAL.outline,
  N: PAL.navyLight,
  n: PAL.navy,
  W: PAL.foam2,
};

const KEY: PixMap = [
  "................",
  "................",
  "................",
  "................",
  "..OOOO..........",
  ".OGggGO.........",
  ".OG..GO.........",
  ".OG..GOOOOOOOOO.",
  ".OGggGGGGGGGGGO.",
  "..OOOO..OGO.OGO.",
  "........OGO.OGO.",
  "........OOO.OOO.",
  "................",
  "..W.............",
  "................",
  "................",
];
const KEY_COLORS: Record<string, string> = {
  O: PAL.goldDark,
  G: PAL.gold,
  g: PAL.goldHi,
  W: PAL.foam,
};

const BERRY: PixMap = [
  "................",
  "................",
  "......GG........",
  ".....GgG........",
  "......g.........",
  "......OOO.......",
  ".....ORRRO......",
  "....ORWRRRO.....",
  "....ORRRRrO.....",
  "....ORRRrrO.....",
  ".....OrrrO......",
  "......OOO.......",
  "................",
  "..s........s....",
  "................",
  "................",
];
const BERRY_COLORS: Record<string, string> = {
  O: PAL.ballOutline,
  R: PAL.red,
  r: PAL.redDark,
  W: PAL.foam,
  G: PAL.leafDark,
  g: PAL.leaf,
  s: PAL.shadow,
};

/** The SURF CHARM: a wave-crested amulet on a cord. */
const CHARM: PixMap = [
  "................",
  "......OO........",
  ".....O..O.......",
  "....OOOOOO......",
  "...OBBBBBBO.....",
  "..OBCCCCCCBO....",
  "..OBCWWCCCBO....",
  "..OBWCCWCCBO....",
  "..OBCCCWWCBO....",
  "..OBCCCCCCBO....",
  "...OBBBBBBO.....",
  "....OOOOOO......",
  "................",
  "..s.........s...",
  "................",
  "................",
];
const CHARM_COLORS: Record<string, string> = {
  O: "#313131",
  B: PAL.discOuter,
  C: PAL.discMid,
  W: PAL.foam,
  s: PAL.shadow,
};

const PEARL: PixMap = [
  "................",
  "................",
  "................",
  "..W.............",
  "................",
  "......OOO.......",
  ".....OWWWO......",
  "....OWwWWWO.....",
  "....OWWWWWO.....",
  ".....OwWWO......",
  "......OOO.......",
  "................",
  ".............W..",
  "................",
  "................",
  "................",
];
const PEARL_COLORS: Record<string, string> = {
  O: PAL.foam4,
  W: PAL.foam,
  w: PAL.foam2,
};

const SIGN: PixMap = [
  "................",
  "................",
  "................",
  ".OOOOOOOOOOOO...",
  ".OffffffffffO...",
  ".OfddddddddfO...",
  ".OffffffffffO...",
  ".OfddddddddfO...",
  ".OffffffffffO...",
  ".OOOOOOOOOOOO...",
  "......Opp.......",
  "......Opp.......",
  "......Opp.......",
  "......OppO......",
  "....ssOOOOss....",
  "................",
];
const SIGN_COLORS: Record<string, string> = {
  O: PAL.woodDark,
  f: PAL.plasterDark,
  d: PAL.woodShade,
  p: PAL.woodShade,
  s: PAL.lawnDarkest,
};

function doorClosedTexture(): Texture {
  const [canvas, ctx] = makeCanvas(16, 16);
  rect(ctx, 0, 0, 16, 16, PAL.woodDark); //          frame
  rect(ctx, 1, 1, 14, 15, PAL.wood); //              door panel
  rect(ctx, 1, 1, 14, 2, PAL.woodLight); //          top highlight
  rect(ctx, 2, 4, 1, 10, PAL.woodShade); //          panel grooves
  rect(ctx, 13, 4, 1, 10, PAL.woodShade);
  rect(ctx, 5, 4, 6, 1, PAL.woodShade);
  rect(ctx, 11, 8, 2, 2, PAL.woodHi); //             knob
  px(ctx, 12, 9, PAL.woodDark);
  rect(ctx, 1, 14, 14, 2, PAL.woodShade); //         threshold
  return Texture.from(canvas);
}

function doorOpenTexture(): Texture {
  const [canvas, ctx] = makeCanvas(16, 16);
  rect(ctx, 0, 0, 16, 16, PAL.woodDark); //          frame
  rect(ctx, 1, 0, 14, 16, PAL.interior); //          dark doorway
  rect(ctx, 2, 13, 12, 3, PAL.navyLight); //         faint floor glow
  rect(ctx, 1, 0, 1, 16, PAL.woodShade); //          jambs
  rect(ctx, 14, 0, 1, 16, PAL.woodShade);
  return Texture.from(canvas);
}

/** A wooden level-gate set into the tree border: posts + planks + an X brace. */
function gateClosedTexture(): Texture {
  const [canvas, ctx] = makeCanvas(16, 16);
  // Posts.
  rect(ctx, 0, 1, 3, 15, PAL.woodDark);
  rect(ctx, 13, 1, 3, 15, PAL.woodDark);
  rect(ctx, 1, 1, 1, 14, PAL.wood);
  rect(ctx, 14, 1, 1, 14, PAL.wood);
  // Post caps.
  rect(ctx, 0, 0, 3, 1, PAL.woodHi);
  rect(ctx, 13, 0, 3, 1, PAL.woodHi);
  // Horizontal planks.
  rect(ctx, 3, 4, 10, 3, PAL.wood);
  rect(ctx, 3, 4, 10, 1, PAL.woodLight);
  rect(ctx, 3, 6, 10, 1, PAL.woodShade);
  rect(ctx, 3, 10, 10, 3, PAL.wood);
  rect(ctx, 3, 10, 10, 1, PAL.woodLight);
  rect(ctx, 3, 12, 10, 1, PAL.woodShade);
  // X brace between the planks.
  for (let i = 0; i < 4; i++) {
    px(ctx, 5 + i, 7 + i, PAL.woodDark);
    px(ctx, 10 - i, 7 + i, PAL.woodDark);
  }
  return Texture.from(canvas);
}

/** The same gate, swung open: posts only, the way ahead clear. */
function gateOpenTexture(): Texture {
  const [canvas, ctx] = makeCanvas(16, 16);
  rect(ctx, 0, 1, 3, 15, PAL.woodDark);
  rect(ctx, 13, 1, 3, 15, PAL.woodDark);
  rect(ctx, 1, 1, 1, 14, PAL.wood);
  rect(ctx, 14, 1, 1, 14, PAL.wood);
  rect(ctx, 0, 0, 3, 1, PAL.woodHi);
  rect(ctx, 13, 0, 3, 1, PAL.woodHi);
  // The swung-open leaves, edge-on against each post.
  rect(ctx, 3, 3, 1, 11, PAL.woodShade);
  rect(ctx, 12, 3, 1, 11, PAL.woodShade);
  return Texture.from(canvas);
}

/** A desk PC, Gen-3 style: monitor with a glowing screen on a small desk. */
function computerTexture(): Texture {
  const [canvas, ctx] = makeCanvas(16, 16);
  // Desk.
  rect(ctx, 1, 10, 14, 5, PAL.woodDark);
  rect(ctx, 2, 11, 12, 3, PAL.woodLight);
  rect(ctx, 2, 11, 12, 1, PAL.woodHi);
  // Monitor.
  rect(ctx, 3, 1, 10, 9, PAL.outline);
  rect(ctx, 4, 2, 8, 6, PAL.navy);
  rect(ctx, 5, 3, 6, 4, PAL.glassHi);
  rect(ctx, 5, 3, 6, 1, PAL.foam); //   screen glare
  px(ctx, 6, 5, PAL.navyDark); //       a 9×9 grid hint on screen
  px(ctx, 8, 5, PAL.navyDark);
  px(ctx, 10, 5, PAL.navyDark);
  // Stand + keyboard.
  rect(ctx, 7, 9, 2, 1, PAL.outline);
  rect(ctx, 5, 10, 6, 1, PAL.navyLight);
  return Texture.from(canvas);
}

/** A tall wooden closet, doors shut. */
function closetTexture(): Texture {
  const [canvas, ctx] = makeCanvas(16, 16);
  rect(ctx, 1, 0, 14, 15, PAL.woodDark); //  body
  rect(ctx, 2, 1, 12, 13, PAL.wood); //      face
  rect(ctx, 2, 1, 12, 1, PAL.woodHi); //     top trim
  rect(ctx, 7, 1, 1, 13, PAL.woodDark); //   door split
  rect(ctx, 5, 6, 1, 3, PAL.woodHi); //      knobs
  rect(ctx, 9, 6, 1, 3, PAL.woodHi);
  rect(ctx, 2, 13, 12, 1, PAL.woodShade); // base shadow
  return Texture.from(canvas);
}

/** The closet with its doors open: hanging clothes inside. */
function closetOpenTexture(): Texture {
  const [canvas, ctx] = makeCanvas(16, 16);
  rect(ctx, 1, 0, 14, 15, PAL.woodDark); //  body
  rect(ctx, 3, 1, 10, 13, PAL.interior); //  dark inside
  // Open door leaves, edge-on.
  rect(ctx, 1, 1, 2, 13, PAL.wood);
  rect(ctx, 13, 1, 2, 13, PAL.wood);
  // Hanger rail + clothes.
  rect(ctx, 3, 2, 10, 1, PAL.woodShade);
  rect(ctx, 4, 3, 2, 6, PAL.navyLight);
  rect(ctx, 7, 3, 2, 7, PAL.red);
  rect(ctx, 10, 3, 2, 5, PAL.foam2);
  return Texture.from(canvas);
}

export interface ArtKit {
  player: PlayerSprites;
  /** Walkable actors by sprite id ("player", "aanya", "aanya_cry"). */
  actors: Record<string, PlayerSprites>;
  blob: Texture;
  objects: Record<string, Texture>;
}

let kit: ArtKit | null = null;

export function getArtKit(): ArtKit {
  if (kit) return kit;
  const player = buildPlayer();
  kit = {
    player,
    actors: {
      player,
      aanya: buildAanya(),
      aanya_cry: buildAanyaCry(),
    },
    blob: textureFromPixMap(SURF_BLOB, BLOB_COLORS, 16, 16),
    objects: {
      key: textureFromPixMap(KEY, KEY_COLORS, 16, 16),
      berry: textureFromPixMap(BERRY, BERRY_COLORS, 16, 16),
      charm: textureFromPixMap(CHARM, CHARM_COLORS, 16, 16),
      pearl: textureFromPixMap(PEARL, PEARL_COLORS, 16, 16),
      sign: textureFromPixMap(SIGN, SIGN_COLORS, 16, 16),
      door_closed: doorClosedTexture(),
      door_open: doorOpenTexture(),
      gate_closed: gateClosedTexture(),
      gate_open: gateOpenTexture(),
      computer: computerTexture(),
      closet: closetTexture(),
      closet_open: closetOpenTexture(),
    },
  };
  return kit;
}
